// react
import { useEffect, useMemo, useRef } from 'react';
import {
  useLocation,
  useMatches,
  useNavigate,
  useOutlet,
} from 'react-router-dom';
import { ROUTES } from 'router';

// hooks
import { useNavigationDirection } from '@karehero/llama/pwa/hooks/useNavigationDirection';

// mui
import { Box, Stack, useTheme } from '@mui/material';

// framer-motion
import { AnimatePresence, motion, type Variants } from 'framer-motion';

// components
import { NavBar } from '@karehero/llama/pwa/molecules/NavBar/NavBar';
import { type AccountInfoProps } from '@karehero/llama/pwa/atoms/AccountInfo/AccountInfo';
import { Banner } from '@karehero/llama/components/atoms/Banner/Banner';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// utils
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';
import { formatRoleName } from '@karehero/llama/utils/format';

// store
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { apiUrl } from 'store/api';
import { useGetNotificationsQuery } from 'store/api/notification';

// hooks
import { useApp, useAuth, useCareCircle } from 'hooks';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useEventStream } from 'hooks/useEventStream';

const rootStackVariants: Variants = {
  initial: (isBack: boolean) => ({
    x: isBack ? '-25%' : '25%',
    opacity: 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
  },
  exit: (isBack: boolean) => ({
    x: isBack ? '25%' : '-25%',
    opacity: 0,
    zIndex: isBack ? 1300 : 1100,
  }),
};

/**
 * Root sets up the event stream and app store banner.
 * NavBar is rendered here for all authenticated routes.
 * Child layout routes RootChrome handle their own chrome.
 */
export const Root = () => {
  // hooks
  const theme = useTheme();
  const navigate = useNavigate();
  const currentOutlet = useOutlet();
  const location = useLocation();
  const account = useSelector(getAccount);
  const { isApp, isMobileBrowser } = useApp();
  const { signOut, getToken } = useAuth();
  const { startEventStream } = useEventStream();
  const { flags } = useFeatureFlags();
  const matches = useMatches();
  const {
    switchCareCircle,
    careCircleMember,
    accountCareCircles,
    careCircle: currentCareCircle,
  } = useCareCircle();
  const { data: notificationsData } = useGetNotificationsQuery(undefined, {
    skip: !account,
  });

  // Detect stacked routes at any level (NavBar hiding)
  const isStacked = matches.some((m) => {
    const h = m.handle as { stack?: boolean; rootStack?: boolean } | undefined;
    return h?.stack || h?.rootStack;
  });

  // Detect root-level stacked routes (overlay rendered by Root)
  const isRootStacked = matches.some(
    (m) => (m.handle as { rootStack?: boolean } | undefined)?.rootStack,
  );

  // Stable key per layout — only changes on cross-layout navigation.
  // Derived from route handle.layout so it works regardless of URL shape.
  const layoutKey =
    (matches.find((m) => (m.handle as any)?.layout)?.handle as any)?.layout ||
    'root';

  // Keep a frozen copy of the last non-root-stacked outlet so the
  // chrome template stays visible underneath the overlay.
  const lastOutletRef = useRef<React.ReactNode>(null);
  if (!isRootStacked && currentOutlet) {
    lastOutletRef.current = currentOutlet;
  }

  const isBack = useNavigationDirection();

  useEffect(() => {
    if (!account) return;
    return startEventStream({ getToken });
  }, [account]);

  const homePath = useMemo(() => {
    if (!account || account.accountRole.name !== 'KareHero Administrator') {
      return ROUTES.HOME;
    }
    return ROUTES.ADMIN;
  }, [account]);

  const accountInfoProps: AccountInfoProps = useMemo(
    () => ({
      role: formatRoleName(account?.accountRole?.name) || 'User',
      name: `${account?.firstName || ''} ${account?.lastName || ''}`,
      fallback: `${account?.firstName?.[0] || ''}${
        account?.lastName?.[0] || ''
      }`,
      avatarSrc: getProfilePictureUrl(account),
      hideLabel: true,
      toAccountSettings: ROUTES.SETTINGS,
      onSignOut: signOut,
      navigate,
      isMultipleCareCircles: !!flags.isMultipleCareCircles,
      careCircles: accountCareCircles || [],
      currentCareCircleMember: careCircleMember,
      onCareCircleChange: switchCareCircle,
      currentCareCircle,
      onNewCareCircle: () => navigate(ROUTES.CARE_CIRCLE_NEW),
    }),
    [
      account,
      signOut,
      navigate,
      flags.isMultipleCareCircles,
      accountCareCircles,
      careCircleMember,
      switchCareCircle,
      currentCareCircle,
    ],
  );

  const unreadCount = useMemo(
    () =>
      notificationsData?.notifications?.filter((n) => !n.readAt).length ?? 0,
    [notificationsData],
  );

  const navBar = account ? (
    <NavBar
      accountInfoProps={accountInfoProps}
      onLogoClick={() => navigate(homePath)}
      onNotificationsClick={() => navigate(ROUTES.NOTIFICATIONS)}
      unreadCount={unreadCount}
    />
  ) : null;

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <motion.div
        initial={false}
        animate={{
          x: isStacked ? '-25%' : '0%',
          opacity: isStacked ? 0 : 1,
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        {navBar}
      </motion.div>
      {!isApp && isMobileBrowser && flags.appStoreBanner && (
        <Box sx={{ position: 'relative', zIndex: 1300 }}>
          <Banner
            to={`${apiUrl}/v1/app-store`}
            content={
              <>
                <div>
                  Download our app <u>here</u>
                </div>
                <Icon icon='arrow-down-to-bracket' fontSize={18} />
              </>
            }
          />
        </Box>
      )}
      <motion.div
        initial={false}
        animate={{
          x: isRootStacked ? '-25%' : '0%',
          opacity: isRootStacked ? 0 : 1,
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
        style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
      >
        <AnimatePresence mode='wait' initial={false}>
          <motion.div
            key={isRootStacked ? '_frozen' : layoutKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ height: '100%' }}
          >
            <Box
              sx={{
                height: '100%',
                overflowY: 'auto',
              }}
            >
              {isRootStacked ? lastOutletRef.current : currentOutlet}
            </Box>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <AnimatePresence initial={false} custom={isBack}>
        {isRootStacked && (
          <motion.div
            key={location.pathname}
            custom={isBack}
            variants={rootStackVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1200,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {currentOutlet}
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
};

Root.displayName = 'Root';
export default Root;
